import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { auth, db } from "../../firebase";
import { useTheme } from "../../context/ThemeContext";
import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy
} from "firebase/firestore";

export default function SquadsList({ onSelect, activeId }) {
    const [squads, setSquads] = useState([]);
    const [loading, setLoading] = useState(true);
    const currentUid = auth.currentUser?.uid;
    const { isDarkMode } = useTheme();

    // 🎨 Dynamic Classes
    const textPrimary = isDarkMode ? "text-white/90" : "text-gray-800";
    const textSecondary = isDarkMode ? "text-white/60" : "text-gray-500";
    const listHover = isDarkMode ? "hover:bg-white/10 hover:border-white/5" : "hover:bg-black/5 hover:border-black/5";
    const listActive = isDarkMode ? "bg-white/20 shadow-lg border-white/10" : "bg-white shadow-md border-gray-200";

    useEffect(() => {
        if (!currentUid) return;

        const q = query(
            collection(db, "squads"),
            where("members", "array-contains", currentUid),
            orderBy("lastMessageTimestamp", "desc")
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const rawSquads = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
            setSquads(rawSquads);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching squads:", error);
            // Fallback if index missing or other error, try client side sort if needed or just simple query
            if (error.code === 'failed-precondition') {
                // Retry without orderBy if index is missing
                const qSimple = query(
                    collection(db, "squads"),
                    where("members", "array-contains", currentUid)
                );
                onSnapshot(qSimple, (snap) => {
                    const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                    // Manual sort
                    list.sort((a, b) => (b.lastMessageTimestamp?.seconds || 0) - (a.lastMessageTimestamp?.seconds || 0));
                    setSquads(list);
                    setLoading(false);
                });
            } else {
                setLoading(false);
            }
        });

        return unsubscribe;
    }, [currentUid]);

    return (
        <div className="p-4 h-full flex flex-col">
            <div className="flex justify-between items-center mb-4">
                <h2 className={`text-2xl font-bold tracking-tight ${isDarkMode ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400' : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600'}`}>Squads</h2>
                {/* Create Squad Button Placeholder - will be handled by parent or a button here calling parent prop */}
            </div>

            <AnimatePresence mode="wait">
                {loading ? (
                    <motion.div
                        key="loader"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="flex justify-center mt-10"
                    >
                        <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDarkMode ? 'border-white' : 'border-purple-500'}`}></div>
                    </motion.div>
                ) : squads.length === 0 ? (
                    <motion.div
                        key="empty"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`text-center mt-10 text-sm ${textSecondary}`}
                    >
                        You haven't joined any Squads yet.<br /> Create one or join via link!
                    </motion.div>
                ) : (
                    <motion.ul
                        key="list"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ staggerChildren: 0.1 }}
                        className="space-y-2 overflow-y-auto custom-scrollbar flex-1"
                    >
                        {squads.map((squad) => (
                            <motion.li
                                key={squad.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`p-3 rounded-xl cursor-pointer transition-all border border-transparent flex items-center gap-3
                ${activeId === squad.id ? listActive : listHover}`}
                                onClick={() => onSelect({ ...squad, isSquad: true, username: squad.name, photoURL: squad.photoURL, uid: squad.id })}
                            >
                                <div className="relative shrink-0">
                                    {squad.photoURL ? (
                                        <img
                                            src={squad.photoURL}
                                            alt={squad.name}
                                            className={`w-12 h-12 rounded-full object-cover shadow-md border ${isDarkMode ? 'border-white/20' : 'border-gray-200'}`}
                                        />
                                    ) : (
                                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-pink-500 flex items-center justify-center text-white font-bold shadow-md text-lg">
                                            {squad.name?.[0]?.toUpperCase()}
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex justify-between items-center mb-0.5">
                                        <p className={`font-semibold truncate text-base ${textPrimary}`}>{squad.name}</p>
                                        {squad.lastMessageTimestamp && (
                                            <span className={`text-[10px] font-medium whitespace-nowrap ml-2 opacity-80 ${textSecondary}`}>
                                                {new Date(squad.lastMessageTimestamp.seconds * 1000).toLocaleDateString()}
                                            </span>
                                        )}
                                    </div>
                                    <p className={`text-sm truncate pr-2 opacity-90 ${textSecondary}`}>
                                        {squad.lastMessage?.text || "No messages yet"}
                                    </p>
                                </div>
                            </motion.li>
                        ))}
                    </motion.ul>
                )}
            </AnimatePresence>
        </div>
    );
}
